import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FiArrowLeft,
  FiCpu,
  FiDatabase,
  FiExternalLink,
  FiEye,
  FiFileText,
  FiGrid,
  FiHash,
  FiAlignJustify,
  FiLayers,
  FiList,
  FiMapPin,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiTarget,
  FiX
} from 'react-icons/fi';
import {
  formatEnumLabel,
  formatSectorLabel,
  mapSectorToCategory,
  normalizeAssetClassTokenStandard,
  TOKENIZER_PLATFORM_SECTORS
} from '../../api/smartTokenizerMappers';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { useClientConfig } from '../../contexts/ClientConfigContext';
import { useTokenization } from '../../contexts/TokenizationContext';
import { getStoredSession, subscribeToSessionChanges } from '../../api/authStorage';
import { SearchableFilterSelect } from '../Admin/SearchableFilterSelect';
import BulkTokenizeWizardModal from './BulkTokenizeWizardModal';
import BulkSimilarAssetsModal from './BulkSimilarAssetsModal';
import {
  bulkRegistrationMembers,
  bulkTotalDocCount,
  isMintReadyApiAsset,
  preferredMintRequestForDisplay,
  shortenTxHash
} from '../../utils/bulkRegistrationMintHelpers';

const ManagerContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%);
  color: #fff;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #9bd6ff;
  text-decoration: none;
  margin-bottom: 1.5rem;
  font-weight: 500;
  &:hover {
    color: #c4e4ff;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 2.25rem;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const HeroSubtitle = styled.p`
  font-size: 1.05rem;
  color: #a0a0a0;
  max-width: 820px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Gate = styled.div`
  text-align: center;
  padding: 3rem;
  color: #fecaca;
  background: rgba(255, 80, 80, 0.08);
  border-radius: 16px;
  border: 1px solid rgba(255, 100, 100, 0.25);
`;

const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatTile = styled.div`
  padding: 1rem 1.15rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  text-align: center;
  .v {
    font-size: 1.5rem;
    font-weight: 800;
    color: #4facfe;
  }
  .l {
    font-size: 0.75rem;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 0.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  .icon {
    color: #4facfe;
  }
`;

const ActionsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${(p) => (p.$primary ? 'linear-gradient(135deg, #4facfe, #00f2fe)' : 'rgba(255, 255, 255, 0.1)')};
  color: #fff;
  border: ${(p) => (p.$primary ? 'none' : '1px solid rgba(255, 255, 255, 0.2)')};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    transform: translateY(-1px);
    opacity: ${(p) => (p.$primary ? 0.95 : 1)};
    background: ${(p) =>
      p.$primary ? 'linear-gradient(135deg, #3d8bfe, #00d4fe)' : 'rgba(255, 255, 255, 0.16)'};
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
  }
`;

const ToolbarRegistryLink = styled(Link)`
  color: #9bd6ff;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  &:hover {
    color: #c4e4ff;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
`;

const FilterSearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 220px;
  max-width: 400px;
  color: #94a3b8;
`;

const FilterTextInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.6rem 0.75rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  &::placeholder {
    color: #64748b;
  }
  &:focus {
    outline: none;
    border-color: #4facfe;
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 160px;
  label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
  }
`;

const FilterMeta = styled.span`
  font-size: 0.85rem;
  color: #64748b;
  margin-left: auto;
  align-self: center;
`;

const RegistryToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
`;

const LayoutLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
`;

const ViewToggleGroup = styled.div`
  display: inline-flex;
  align-items: stretch;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.28);
  overflow: hidden;
`;

const ViewToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.8rem;
  border: none;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: ${(p) =>
    p.$active ? 'linear-gradient(135deg, rgba(79, 172, 254, 0.32), rgba(0, 242, 254, 0.12))' : 'transparent'};
  color: ${(p) => (p.$active ? '#e0f2fe' : '#94a3b8')};
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;
  white-space: nowrap;

  &:last-child {
    border-right: none;
  }

  &:hover {
    color: #e2e8f0;
    background: ${(p) => (p.$active ? undefined : 'rgba(79, 172, 254, 0.1)')};
  }

  svg {
    flex-shrink: 0;
    opacity: ${(p) => (p.$active ? 1 : 0.75)};
  }
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 1.5rem;
`;

const AssetCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.75rem;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-3px);
    border-color: #4facfe;
    box-shadow: 0 10px 30px rgba(79, 172, 254, 0.12);
  }
`;

const CardTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const CardMeta = styled.div`
  font-size: 0.88rem;
  color: #94a3b8;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const Badge = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.2);
  color: #cbd5e1;
  &.accent {
    color: #7dd3fc;
    border-color: rgba(125, 211, 252, 0.35);
    background: rgba(56, 189, 248, 0.1);
  }
  &.ok {
    color: #86efac;
    border-color: rgba(134, 239, 172, 0.35);
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: rgba(79, 172, 254, 0.15);
  color: #9bd6ff;
  border: 1px solid rgba(79, 172, 254, 0.35);
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const CardBtn = styled.button`
  flex: 1;
  padding: 0.65rem;
  border-radius: 8px;
  border: 1px solid rgba(79, 172, 254, 0.35);
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  color: #fff;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  &:hover {
    opacity: 0.95;
  }
`;

const OutlineCardBtn = styled.button`
  flex: 1;
  padding: 0.65rem;
  border-radius: 8px;
  border: 1px solid rgba(125, 211, 252, 0.45);
  background: rgba(79, 172, 254, 0.08);
  color: #bae6fd;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  &:hover {
    background: rgba(79, 172, 254, 0.16);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const IconGhost = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.06);
  color: #9bd6ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.15);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const DataTableWrap = styled.div`
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  overflow-x: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const DataTable = styled.table`
  width: 100%;
  min-width: 1040px;
  border-collapse: collapse;
  font-size: 0.86rem;

  th,
  td {
    text-align: left;
    padding: 0.8rem 0.95rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;
  }

  th {
    font-size: 0.66rem;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #94a3b8;
    font-weight: 700;
    background: rgba(0, 0, 0, 0.38);
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr {
    transition: background 0.15s;
  }

  tbody tr:hover {
    background: rgba(79, 172, 254, 0.07);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const TableAssetTitle = styled.div`
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 0.15rem;
`;

const TableMuted = styled.span`
  font-size: 0.72rem;
  color: #64748b;
  font-family: ui-monospace, monospace;
`;

const TableActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-end;
`;

const TableIconBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid rgba(79, 172, 254, 0.35);
  background: rgba(79, 172, 254, 0.12);
  color: #9bd6ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    background: rgba(79, 172, 254, 0.22);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const CompactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const CompactRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.65rem 1rem;
  align-items: center;
  padding: 0.85rem 1.1rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  &:hover {
    border-color: rgba(79, 172, 254, 0.38);
    background: rgba(79, 172, 254, 0.06);
    box-shadow: 0 4px 20px rgba(79, 172, 254, 0.08);
  }

  .cr-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.35rem;
  }

  .cr-title {
    font-weight: 700;
    color: #fff;
    font-size: 0.98rem;
  }

  .cr-meta {
    font-size: 0.78rem;
    color: #94a3b8;
    line-height: 1.55;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
  }

  .cr-dot {
    opacity: 0.45;
  }

  .cr-actions {
    display: flex;
    gap: 0.4rem;
    flex-shrink: 0;
    justify-content: flex-end;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1.25rem;
`;

const ModalCard = styled(motion.div)`
  background: linear-gradient(145deg, #151528 0%, #1a1a38 100%);
  border: 1px solid rgba(79, 172, 254, 0.28);
  border-radius: 20px;
  max-width: 760px;
  width: 100%;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  h2 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 700;
  }
`;

const ModalBody = styled.div`
  padding: 1.15rem 1.25rem 1.35rem;
  overflow-y: auto;
`;

/** Matches Core Tokenizer bulk-similar modal width. */
const BulkSimilarModalFrame = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  max-width: 960px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    &:hover {
      background: rgba(79, 172, 254, 0.45);
    }
  }
`;

const pick = (obj, ...keys) => {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

const ASSET_STATUS_NAMES = [
  '',
  'Draft',
  'SubmittedForLegalReview',
  'UnderLegalReview',
  'ApprovedForTokenization',
  'Rejected',
  'MetadataPinnedToIpfs',
  'MintRequested',
  'MintingInProgress',
  'Minted',
  'VaultLockInProgress',
  'LockedInVault',
  'FractionalizationInProgress',
  'Fractionalized'
];

function assetStatusCanonical(status) {
  if (status === null || status === undefined) return 'Unknown';
  if (typeof status === 'number') {
    if (status >= 1 && status < ASSET_STATUS_NAMES.length) return ASSET_STATUS_NAMES[status];
    return `Unknown(${status})`;
  }
  const str = String(status).trim();
  if (!str) return 'Unknown';
  const exact = ASSET_STATUS_NAMES.find((n) => n === str);
  if (exact) return exact;
  const fold = str.replace(/\s+/g, '');
  const ci = ASSET_STATUS_NAMES.find((n) => n.toLowerCase() === fold.toLowerCase());
  if (ci) return ci;
  return str;
}

function legalReviewsOf(raw) {
  const l = raw?.legalReviews ?? raw?.LegalReviews;
  return Array.isArray(l) ? l : [];
}

function legalStatusCanonical(s) {
  if (s === null || s === undefined) return '';
  if (typeof s === 'number') {
    const LEGAL = ['', 'PendingAssignment', 'InReview', 'Approved', 'Rejected'];
    return LEGAL[s] || String(s);
  }
  return String(s).trim();
}

function summarizeLegalAcrossBulk(br) {
  const members = bulkRegistrationMembers(br);
  const seen = new Set();
  const parts = [];
  for (const m of members) {
    for (const lr of legalReviewsOf(m)) {
      const id = lr?.id ?? lr?.Id;
      const key = id ? String(id) : JSON.stringify(lr);
      if (seen.has(key)) continue;
      seen.add(key);
      parts.push(formatEnumLabel(legalStatusCanonical(lr.status ?? lr.Status)));
    }
  }
  if (parts.length === 0) return 'No legal reviews on members';
  return parts.join(', ');
}

function bulkAggregateStatusLabel(br) {
  const members = bulkRegistrationMembers(br);
  if (members.length === 0) return 'Empty batch';
  const canon = members.map((m) => assetStatusCanonical(m.status ?? m.Status));
  if (canon.every((s) => s === 'ApprovedForTokenization')) return 'ApprovedForTokenization';
  if (canon.every((s) => s === 'MetadataPinnedToIpfs')) return 'MetadataPinnedToIpfs';
  if (canon.some((s) => s === 'Minted' || s === 'MintRequested')) return 'Mint in progress / partial';
  const worst = canon.find((s) => s === 'Draft') || canon[0];
  return `Mixed · ${worst}`;
}

function bulkShowsTokenizeButton(br, assetClass) {
  const members = bulkRegistrationMembers(br);
  if (members.length === 0) return false;
  const allApproved = members.every(
    (m) => assetStatusCanonical(m.status ?? m.Status) === 'ApprovedForTokenization'
  );
  if (!allApproved) return false;
  if (!assetClass?.contractAddress || !String(assetClass.contractAddress).trim()) return false;
  if (normalizeAssetClassTokenStandard(assetClass.tokenStandard) !== 'ERC721') return false;
  return members.every((m) => isMintReadyApiAsset(m));
}

const REGISTRY_VIEW_KEY = 'tokenizerBulkRegistryView';

function readView() {
  try {
    const v = localStorage.getItem(REGISTRY_VIEW_KEY);
    if (['gallery', 'table', 'compact'].includes(v)) return v;
  } catch {
    /* ignore */
  }
  return 'gallery';
}

function clipText(s, max) {
  const t = String(s || '');
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

function TokenizerBulkAssetsRegistry() {
  const location = useLocation();
  const isAdminRegistryRoute = location.pathname.startsWith('/admin/bulk-assets-registry');
  const [hasSession, setHasSession] = useState(() => Boolean(getStoredSession()?.accessToken));
  const { isAuthorizedTokenizer, isAdmin, currentIssuer } = useTokenIssuer();
  const { listAssetBulkRegistrations, listBulkMemberMintHistory } = useTokenization();
  const { assetClasses, loadAssetClasses, getAssetClassById } = useAssetClass();
  const listBulkRef = useRef(listAssetBulkRegistrations);
  listBulkRef.current = listAssetBulkRegistrations;
  const loadAssetClassesRef = useRef(loadAssetClasses);
  loadAssetClassesRef.current = loadAssetClasses;
  const { addressExplorerUrl, explorerConfigured, transactionExplorerUrl } = useClientConfig();

  const [bulks, setBulks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [registryView, setRegistryView] = useState(readView);
  const [detailBulk, setDetailBulk] = useState(null);
  const [detailMintHistory, setDetailMintHistory] = useState([]);
  const [detailMintHistoryLoading, setDetailMintHistoryLoading] = useState(false);
  const [tokenizeState, setTokenizeState] = useState({ open: false, bulk: null, assetClass: null, loading: false });
  const [bulkSimilarOpen, setBulkSimilarOpen] = useState(false);

  const classById = useMemo(() => {
    const m = new Map();
    for (const c of assetClasses) {
      if (c?.id) m.set(String(c.id), c);
    }
    return m;
  }, [assetClasses]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await loadAssetClassesRef.current();
      const raw = await listBulkRef.current();
      setBulks(Array.isArray(raw) ? raw : []);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Failed to load bulk registrations');
      setBulks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const closeBulkSimilarModal = useCallback(() => {
    setBulkSimilarOpen(false);
    load();
  }, [load]);

  useEffect(() => {
    setHasSession(Boolean(getStoredSession()?.accessToken));
    return subscribeToSessionChanges((s) => setHasSession(Boolean(s?.accessToken)));
  }, []);

  useEffect(() => {
    if (hasSession && (isAuthorizedTokenizer || isAdmin)) {
      load();
    }
  }, [hasSession, isAuthorizedTokenizer, isAdmin, load]);

  useEffect(() => {
    try {
      localStorage.setItem(REGISTRY_VIEW_KEY, registryView);
    } catch {
      /* ignore */
    }
  }, [registryView]);

  const detailHistoryByAssetId = useMemo(() => {
    const m = new Map();
    for (const e of detailMintHistory) {
      const id = e?.assetId ?? e?.AssetId;
      if (id == null) continue;
      m.set(String(id), e);
    }
    return m;
  }, [detailMintHistory]);

  useEffect(() => {
    if (!detailBulk) {
      setDetailMintHistory([]);
      setDetailMintHistoryLoading(false);
      return;
    }
    const bid = pick(detailBulk, 'id', 'Id');
    if (!bid) return;
    let cancelled = false;
    (async () => {
      try {
        setDetailMintHistoryLoading(true);
        const data = await listBulkMemberMintHistory(bid);
        if (!cancelled) setDetailMintHistory(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setDetailMintHistory([]);
          toast.error(e.response?.data?.message || e.message || 'Could not load mint details for this batch');
        }
      } finally {
        if (!cancelled) setDetailMintHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailBulk, listBulkMemberMintHistory]);

  const resolveClass = useCallback(
    (br) => {
      const nested = br?.assetClass ?? br?.AssetClass;
      const cid = pick(br, 'assetClassId', 'AssetClassId');
      if (nested && (nested.name || nested.Name || nested.contractAddress)) {
        const id = nested.id ?? nested.Id ?? cid;
        const fromList = id ? classById.get(String(id)) : null;
        return fromList || nested;
      }
      return cid ? classById.get(String(cid)) : null;
    },
    [classById]
  );

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return bulks.filter((br) => {
      const ac = resolveClass(br);
      const sector = br.sector ?? br.Sector;
      if (sectorFilter && mapSectorToCategory(sector) !== sectorFilter) return false;
      const agg = bulkAggregateStatusLabel(br);
      if (statusFilter === 'approved_tokenize') {
        if (!bulkShowsTokenizeButton(br, ac)) return false;
      } else if (statusFilter === 'approved_for_tokenization') {
        const members = bulkRegistrationMembers(br);
        if (!members.length) return false;
        if (!members.every((m) => assetStatusCanonical(m.status ?? m.Status) === 'ApprovedForTokenization')) {
          return false;
        }
      }
      if (!q) return true;
      const title = pick(br, 'title', 'Title') || '';
      const id = String(pick(br, 'id', 'Id') || '');
      const blob = [title, id, ac?.name, formatSectorLabel(sector), agg].join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [bulks, searchQuery, sectorFilter, statusFilter, resolveClass]);

  const bulkStats = useMemo(() => {
    let withContract = 0;
    let readyTokenize = 0;
    let allApprovedBatches = 0;
    let totalMembers = 0;
    for (const br of bulks) {
      const ac = resolveClass(br);
      const members = bulkRegistrationMembers(br);
      totalMembers += members.length;
      if (ac?.contractAddress && String(ac.contractAddress).trim()) withContract += 1;
      if (bulkShowsTokenizeButton(br, ac)) readyTokenize += 1;
      if (
        members.length > 0 &&
        members.every((m) => assetStatusCanonical(m.status ?? m.Status) === 'ApprovedForTokenization')
      ) {
        allApprovedBatches += 1;
      }
    }
    return {
      total: bulks.length,
      totalMembers,
      withContract,
      readyTokenize,
      allApprovedBatches
    };
  }, [bulks, resolveClass]);

  const openTokenize = useCallback(
    async (br) => {
      const cid = pick(br, 'assetClassId', 'AssetClassId');
      if (!cid) {
        toast.error('Bulk batch has no asset class.');
        return;
      }
      setTokenizeState({ open: true, loading: true, bulk: br, assetClass: null });
      try {
        const ac = await getAssetClassById(cid, true);
        if (!ac) throw new Error('Asset class not found');
        setTokenizeState({ open: true, loading: false, bulk: br, assetClass: ac });
      } catch (e) {
        toast.error(e.response?.data?.message || e.message || 'Could not load asset class');
        setTokenizeState({ open: false, loading: false, bulk: null, assetClass: null });
      }
    },
    [getAssetClassById]
  );

  const sectorOptions = useMemo(
    () => [
      { value: '', label: 'All sectors' },
      ...TOKENIZER_PLATFORM_SECTORS.map((s) => ({ value: s.id, label: s.name }))
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All batches' },
      { value: 'approved_for_tokenization', label: 'All members approved' },
      { value: 'approved_tokenize', label: 'Ready to tokenize (ERC-721 + contract)' }
    ],
    []
  );

  if (!hasSession) {
    return (
      <ManagerContainer>
        <Gate>Sign in to view bulk asset batches.</Gate>
      </ManagerContainer>
    );
  }

  if (!isAuthorizedTokenizer && !isAdmin) {
    return (
      <ManagerContainer>
        <Gate>Only authorized tokenizers and administrators can open this registry.</Gate>
      </ManagerContainer>
    );
  }

  return (
    <ManagerContainer>
      <Back to="/">
        <FiArrowLeft aria-hidden />
        Back to dashboard
      </Back>

      <HeroSection>
        <HeroTitle>
          <FiDatabase /> Bulk assets registry
        </HeroTitle>
        <HeroSubtitle>
          {isAdminRegistryRoute
            ? 'Bulk batches across the platform (as returned by the API for your admin session). Filter by sector and batch status, open details, or start bulk tokenization when every member is approved and the class is ERC-721 with a deployed contract.'
            : `Batches you created under Many similar assets. When every member is approved for tokenization and the class is ERC-721 with a deployed contract, use Tokenize to run the offline bulk mint wizard.${
                currentIssuer?.name ? ` Authorized tokenizer: ${currentIssuer.name}.` : ''
              }`}
        </HeroSubtitle>
      </HeroSection>

      <StatsStrip>
        <StatTile>
          <div className="v">{bulkStats.total}</div>
          <div className="l">Bulk batches</div>
        </StatTile>
        <StatTile>
          <div className="v">{bulkStats.totalMembers}</div>
          <div className="l">Total members</div>
        </StatTile>
        <StatTile>
          <div className="v">{bulkStats.withContract}</div>
          <div className="l">Class contract deployed</div>
        </StatTile>
        <StatTile>
          <div className="v">{bulkStats.allApprovedBatches}</div>
          <div className="l">All members approved</div>
        </StatTile>
        <StatTile>
          <div className="v">{bulkStats.readyTokenize}</div>
          <div className="l">Ready to tokenize</div>
        </StatTile>
      </StatsStrip>

      <ActionsSection>
        <SectionTitle style={{ marginBottom: 0 }}>
          <FiTarget className="icon" />
          Registry
        </SectionTitle>
        <RegistryToolbar>
          {(isAuthorizedTokenizer || isAdmin) && (
            <ActionButton type="button" $primary onClick={() => setBulkSimilarOpen(true)}>
              <FiPackage aria-hidden />
              Create many similar assets
            </ActionButton>
          )}
          <LayoutLabel>View</LayoutLabel>
          <ViewToggleGroup role="group" aria-label="Bulk list layout">
            <ViewToggleBtn
              type="button"
              $active={registryView === 'gallery'}
              onClick={() => setRegistryView('gallery')}
              title="Gallery — rich cards for scanning and context"
            >
              <FiGrid aria-hidden />
              Gallery
            </ViewToggleBtn>
            <ViewToggleBtn
              type="button"
              $active={registryView === 'table'}
              onClick={() => setRegistryView('table')}
              title="Table — dense columns for comparison"
            >
              <FiList aria-hidden />
              Table
            </ViewToggleBtn>
            <ViewToggleBtn
              type="button"
              $active={registryView === 'compact'}
              onClick={() => setRegistryView('compact')}
              title="Compact — minimal rows for long lists"
            >
              <FiAlignJustify aria-hidden />
              Compact
            </ViewToggleBtn>
          </ViewToggleGroup>
          <ActionButton type="button" $primary onClick={load} disabled={loading}>
            <FiRefreshCw aria-hidden />
            {loading ? 'Loading…' : 'Refresh'}
          </ActionButton>
          <ToolbarRegistryLink to={isAdminRegistryRoute ? '/admin/assets' : '/my-assets'}>
            {isAdminRegistryRoute ? 'Platform assets registry' : 'Assets Registry'} →
          </ToolbarRegistryLink>
        </RegistryToolbar>
      </ActionsSection>

      <FilterBar>
        <FilterSearchWrap>
          <FiSearch aria-hidden />
          <FilterTextInput
            placeholder="Search title, id, class, sector, status…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search bulk batches"
          />
        </FilterSearchWrap>
        <FilterField>
          <label id="bulk-reg-sector-lbl" htmlFor="bulk-reg-sector">
            Sector
          </label>
          <SearchableFilterSelect
            id="bulk-reg-sector"
            optionsListLabelledBy="bulk-reg-sector-lbl"
            value={sectorFilter}
            onChange={setSectorFilter}
            options={sectorOptions}
            placeholder="All sectors"
          />
        </FilterField>
        <FilterField>
          <label id="bulk-reg-status-lbl" htmlFor="bulk-reg-status">
            Batch filter
          </label>
          <SearchableFilterSelect
            id="bulk-reg-status"
            optionsListLabelledBy="bulk-reg-status-lbl"
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            placeholder="All batches"
          />
        </FilterField>
        {bulks.length > 0 && (
          <FilterMeta>
            Showing {filtered.length} of {bulks.length}
          </FilterMeta>
        )}
      </FilterBar>

      {!loading && bulks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#a0a0a0' }}>
          <FiLayers style={{ fontSize: '4rem', marginBottom: '1rem', color: '#4facfe' }} />
          <h3 style={{ color: '#fff' }}>No bulk batches in the registry</h3>
          <p>
            {isAdminRegistryRoute
              ? 'None yet, or none visible for your account. Tokenizers create batches from Core Tokenizer → Many similar assets.'
              : 'Create one from Core Tokenizer → Many similar assets, or use Create many similar assets above.'}
          </p>
        </div>
      )}

      {!loading && bulks.length > 0 && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#a0a0a0' }}>
          <FiSearch style={{ fontSize: '4rem', marginBottom: '1rem', color: '#4facfe' }} />
          <h3 style={{ color: '#fff' }}>No matching batches</h3>
          <p>Relax filters or clear search to see more results.</p>
        </div>
      )}

      {filtered.length > 0 && registryView === 'gallery' && (
        <AssetGrid>
          {filtered.map((br, index) => {
            const id = pick(br, 'id', 'Id');
            const title = pick(br, 'title', 'Title') || 'Bulk batch';
            const members = bulkRegistrationMembers(br);
            const ac = resolveClass(br);
            const docs = bulkTotalDocCount(br);
            const agg = bulkAggregateStatusLabel(br);
            const canTokenize = bulkShowsTokenizeButton(br, ac);
            return (
              <AssetCard
                key={id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.4) }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <div style={{ minWidth: 0 }}>
                    <CardTitle>{title}</CardTitle>
                    <CardMeta>
                      <FiHash style={{ opacity: 0.5, verticalAlign: 'middle' }} />{' '}
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{clipText(id, 36)}</span>
                      <br />
                      <FiMapPin style={{ opacity: 0.5, verticalAlign: 'middle' }} />{' '}
                      {pick(br, 'country', 'Country') || '—'} · {members.length} member{members.length === 1 ? '' : 's'}
                    </CardMeta>
                  </div>
                  <StatusPill>{formatEnumLabel(agg)}</StatusPill>
                </div>
                <BadgeRow>
                  <Badge className="accent">{formatSectorLabel(br.sector ?? br.Sector)}</Badge>
                  {ac?.contractAddress ? <Badge className="ok">Contract deployed</Badge> : <Badge>No class contract</Badge>}
                  <Badge>
                    <FiFileText style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {docs} doc{docs === 1 ? '' : 's'}
                  </Badge>
                  <Badge>{normalizeAssetClassTokenStandard(ac?.tokenStandard) === 'ERC721' ? 'ERC-721 batch' : 'Not ERC-721'}</Badge>
                </BadgeRow>
                <CardMeta>
                  <strong style={{ color: '#cbd5e1' }}>Asset class</strong> {ac?.name || ac?.Name || clipText(String(pick(br, 'assetClassId', 'AssetClassId')), 14)}
                  <br />
                  <strong style={{ color: '#cbd5e1' }}>Legal (members)</strong> {summarizeLegalAcrossBulk(br)}
                </CardMeta>
                <CardActions>
                  <CardBtn type="button" onClick={() => setDetailBulk(br)}>
                    <FiEye /> View details
                  </CardBtn>
                  {(isAuthorizedTokenizer || isAdmin) && canTokenize ? (
                    <OutlineCardBtn type="button" onClick={() => openTokenize(br)}>
                      <FiLayers /> Tokenize
                    </OutlineCardBtn>
                  ) : (
                    <OutlineCardBtn type="button" disabled title="Requires all members ApprovedForTokenization, ERC-721 class, deployed contract">
                      <FiLayers /> Tokenize
                    </OutlineCardBtn>
                  )}
                  {ac?.contractAddress && explorerConfigured ? (
                    <IconGhost
                      type="button"
                      title="Class contract in explorer"
                      onClick={() => {
                        const url = addressExplorerUrl(ac.contractAddress);
                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <FiExternalLink />
                    </IconGhost>
                  ) : null}
                </CardActions>
              </AssetCard>
            );
          })}
        </AssetGrid>
      )}

      {filtered.length > 0 && registryView === 'table' && (
        <DataTableWrap>
          <DataTable>
            <thead>
              <tr>
                <th>Batch</th>
                <th>Members</th>
                <th>Sector</th>
                <th>Class</th>
                <th>Aggregate status</th>
                <th>Docs</th>
                <th>Contract</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((br) => {
                const id = pick(br, 'id', 'Id');
                const title = pick(br, 'title', 'Title') || '—';
                const members = bulkRegistrationMembers(br);
                const ac = resolveClass(br);
                const docs = bulkTotalDocCount(br);
                const agg = bulkAggregateStatusLabel(br);
                const canTokenize = bulkShowsTokenizeButton(br, ac);
                return (
                  <tr key={id}>
                    <td>
                      <TableAssetTitle>{clipText(title, 48)}</TableAssetTitle>
                      <TableMuted>{clipText(String(id), 24)}</TableMuted>
                    </td>
                    <td>{members.length}</td>
                    <td>{formatSectorLabel(br.sector ?? br.Sector)}</td>
                    <td>{clipText(ac?.name || ac?.Name || '—', 32)}</td>
                    <td>
                      <StatusPill style={{ fontSize: '0.65rem' }}>{formatEnumLabel(agg)}</StatusPill>
                    </td>
                    <td>{docs}</td>
                    <td>{ac?.contractAddress ? <span style={{ color: '#86efac' }}>Deployed</span> : '—'}</td>
                    <td>
                      <TableActions>
                        <TableIconBtn type="button" title="Details" onClick={() => setDetailBulk(br)}>
                          <FiEye />
                        </TableIconBtn>
                        <TableIconBtn
                          type="button"
                          title="Tokenize"
                          disabled={!canTokenize}
                          onClick={() => openTokenize(br)}
                        >
                          <FiLayers />
                        </TableIconBtn>
                        <TableIconBtn
                          type="button"
                          title="Contract"
                          disabled={!ac?.contractAddress || !explorerConfigured}
                          onClick={() => {
                            const url = addressExplorerUrl(ac.contractAddress);
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <FiExternalLink />
                        </TableIconBtn>
                      </TableActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>
        </DataTableWrap>
      )}

      {filtered.length > 0 && registryView === 'compact' && (
        <CompactList>
          {filtered.map((br) => {
            const id = pick(br, 'id', 'Id');
            const title = pick(br, 'title', 'Title') || 'Bulk batch';
            const members = bulkRegistrationMembers(br);
            const ac = resolveClass(br);
            const agg = bulkAggregateStatusLabel(br);
            const canTokenize = bulkShowsTokenizeButton(br, ac);
            return (
              <CompactRow key={id}>
                <div>
                  <div className="cr-head">
                    <span className="cr-title">{title}</span>
                    <StatusPill style={{ fontSize: '0.65rem' }}>{formatEnumLabel(agg)}</StatusPill>
                  </div>
                  <div className="cr-meta">
                    <span>{members.length} members</span>
                    <span className="cr-dot">·</span>
                    <span>{formatSectorLabel(br.sector ?? br.Sector)}</span>
                    <span className="cr-dot">·</span>
                    <span>{ac?.name || ac?.Name || 'Class'}</span>
                  </div>
                </div>
                <div className="cr-actions">
                  <TableIconBtn type="button" title="Details" onClick={() => setDetailBulk(br)}>
                    <FiEye />
                  </TableIconBtn>
                  <TableIconBtn type="button" title="Tokenize" disabled={!canTokenize} onClick={() => openTokenize(br)}>
                    <FiLayers />
                  </TableIconBtn>
                </div>
              </CompactRow>
            );
          })}
        </CompactList>
      )}

      <AnimatePresence>
        {detailBulk && (
          <ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDetailBulk(null)}>
            <ModalCard
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 980 }}
            >
              <ModalHead>
                <h2>{pick(detailBulk, 'title', 'Title') || 'Bulk batch'}</h2>
                <IconGhost type="button" onClick={() => setDetailBulk(null)} aria-label="Close">
                  <FiX />
                </IconGhost>
              </ModalHead>
              <ModalBody>
                <p style={{ color: '#94a3b8', fontSize: '0.86rem', marginTop: 0 }}>
                  ID: <code style={{ color: '#e2e8f0' }}>{pick(detailBulk, 'id', 'Id')}</code>
                </p>
                <p style={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                  Legal summary: {summarizeLegalAcrossBulk(detailBulk)}
                </p>
                {detailMintHistoryLoading ? (
                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: '0.75rem 0 0' }}>
                    Loading on-chain mint details…
                  </p>
                ) : null}
                <DataTableWrap style={{ marginTop: '1rem' }}>
                  <DataTable style={{ minWidth: 720 }}>
                    <thead>
                      <tr>
                        <th>Asset #</th>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Token ID</th>
                        <th>Mint transaction</th>
                        <th style={{ textAlign: 'center', width: 56 }}>Chain</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkRegistrationMembers(detailBulk).map((m, i) => {
                        const aid = pick(m, 'id', 'Id');
                        const entry = aid != null ? detailHistoryByAssetId.get(String(aid)) : null;
                        const mints = entry?.mintRequests ?? entry?.MintRequests ?? [];
                        const rowMint = preferredMintRequestForDisplay(mints);
                        const txRaw = String(rowMint?.transactionHash ?? rowMint?.TransactionHash ?? '').trim();
                        const txShort = txRaw ? shortenTxHash(txRaw) : '';
                        const tokenId = String(rowMint?.tokenId ?? rowMint?.TokenId ?? '').trim();
                        const canOpenTx = explorerConfigured && Boolean(txRaw);
                        return (
                          <tr key={aid || i}>
                            <td>{pick(m, 'assetNumber', 'AssetNumber')}</td>
                            <td>{pick(m, 'title', 'Title')}</td>
                            <td>{formatEnumLabel(assetStatusCanonical(m.status ?? m.Status))}</td>
                            <td>
                              {tokenId ? (
                                <code style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>{tokenId}</code>
                              ) : (
                                <span style={{ color: '#64748b' }}>—</span>
                              )}
                            </td>
                            <td>
                              {txShort ? (
                                <code style={{ color: '#cbd5e1', fontSize: '0.78rem' }} title={txRaw}>
                                  {txShort}
                                </code>
                              ) : (
                                <span style={{ color: '#64748b' }}>—</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <TableIconBtn
                                type="button"
                                title={
                                  canOpenTx
                                    ? 'View mint transaction in block explorer'
                                    : explorerConfigured
                                      ? 'No transaction hash yet'
                                      : 'Block explorer not configured'
                                }
                                aria-label="View mint transaction in block explorer"
                                disabled={!canOpenTx}
                                onClick={() => {
                                  if (!canOpenTx) return;
                                  const url = transactionExplorerUrl(txRaw);
                                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                }}
                              >
                                <FiExternalLink />
                              </TableIconBtn>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </DataTable>
                </DataTableWrap>
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {bulkSimilarOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeBulkSimilarModal}
          >
            <BulkSimilarModalFrame
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <BulkSimilarAssetsModal onClose={closeBulkSimilarModal} />
            </BulkSimilarModalFrame>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <BulkTokenizeWizardModal
        open={tokenizeState.open && Boolean(tokenizeState.bulk) && Boolean(tokenizeState.assetClass) && !tokenizeState.loading}
        bulkRegistration={tokenizeState.bulk}
        assetClass={tokenizeState.assetClass}
        onClose={() => setTokenizeState({ open: false, bulk: null, assetClass: null, loading: false })}
        onCompleted={() => {
          load();
        }}
      />

      {tokenizeState.loading && tokenizeState.open && (
        <ModalOverlay>
          <ModalCard style={{ maxWidth: 400, textAlign: 'center', padding: '2rem' }}>
            <FiCpu style={{ fontSize: '2rem', color: '#4facfe', marginBottom: '0.75rem' }} />
            <p style={{ color: '#94a3b8', margin: 0 }}>Loading asset class…</p>
          </ModalCard>
        </ModalOverlay>
      )}
    </ManagerContainer>
  );
}

export default TokenizerBulkAssetsRegistry;
